<script setup lang="ts">
import {
  computed,
} from "vue";
import {
  Check,
} from "@lucide/vue";
import {
  formatNumber,
  formatPrice,
} from "~/utils/formatters";

const props = withDefaults(
  defineProps<{
    title: string;
    credits: number;
    creditsLabel: string;
    priceLabel: string;
    balanceText: string;
    upToLabel: string;
    interviewTrainingsLabel: string;
    dialogTrainingsLabel: string;
    capacityUnavailableText: string;
    interviewCapacity: number | null;
    dialogCapacity: number | null;
    price: number | string;
    originalPrice?: number | string | null;
    isSelected?: boolean;
    buttonText?: string;
    buttonDisabled?: boolean;
    buttonType?: "primary" | "success" | "default" | "warning" | "info" | "danger";
    wholeCardClickable?: boolean;
    compactOnMobile?: boolean;
  }>(),
  {
    isSelected: false,
    originalPrice: null,
    buttonText: "Choose Addon",
    buttonDisabled: false,
    buttonType: "primary",
    wholeCardClickable: false,
    compactOnMobile: false,
  },
);

const formattedPrice = computed(() => {
  if (typeof props.price === "number") {
    return formatPrice(props.price);
  }
  return props.price;
});

const formattedOriginalPrice = computed(() => {
  if (props.originalPrice === null || props.originalPrice === undefined) {
    return "";
  }
  return typeof props.originalPrice === "number" ? formatPrice(props.originalPrice) : props.originalPrice;
});

const formattedCredits = computed(() => formatNumber(props.credits));
const formattedInterviewCapacity = computed(() => props.interviewCapacity === null
  ? ""
  : formatNumber(props.interviewCapacity));
const formattedDialogCapacity = computed(() => props.dialogCapacity === null
  ? ""
  : formatNumber(props.dialogCapacity));
const hasTrainingCapacity = computed(() =>
  props.interviewCapacity !== null && props.dialogCapacity !== null,
);

const selectAddon = () => {
  if (!props.buttonDisabled) {
    emit("select");
  }
};

const emit = defineEmits<{
  (e: "select"): void;
}>();
</script>

<template>
  <div
    class="relative flex h-full flex-col overflow-hidden rounded-2xl border-2 bg-white font-sans transition-all duration-300 dark:bg-gray-800"
    :class="[
      isSelected
        ? 'border-green-600 shadow-md ring-2 ring-green-600/10 dark:border-green-500 dark:ring-green-500/20'
        : 'border-gray-200 hover:border-blue-300 hover:shadow-lg dark:border-gray-700 dark:hover:border-blue-700',
      wholeCardClickable && !buttonDisabled ? 'cursor-pointer' : '',
    ]"
    @click="wholeCardClickable && selectAddon()"
  >
    <div
      class="flex min-h-[88px] items-center gap-3 border-b border-blue-100 bg-blue-50/80 dark:border-blue-900/60 dark:bg-blue-950/30"
      :class="compactOnMobile ? 'px-4 py-3 sm:px-5 sm:py-4' : 'px-5 py-4'"
    >
      <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-blue-100 dark:bg-gray-900 dark:ring-blue-900/70">
        <img
          src="/coin.png"
          alt=""
          class="h-9 w-9 object-contain"
        >
      </div>

      <div class="min-w-0">
        <h3 class="text-lg font-bold leading-tight text-gray-900 dark:text-white">
          {{ title }}
        </h3>
      </div>
    </div>

    <div
      class="flex flex-grow flex-col"
      :class="compactOnMobile ? 'p-4 sm:p-5' : 'p-5'"
    >
      <div class="flex items-baseline gap-2">
        <span class="text-4xl font-extrabold tracking-tight text-gray-950 dark:text-white">
          {{ formattedCredits }}
        </span>
        <span class="text-lg font-bold text-gray-800 dark:text-gray-200">
          {{ creditsLabel }}
        </span>
      </div>

      <div class="my-4 border-t border-gray-200 dark:border-gray-700" />

      <div class="text-sm font-medium text-gray-500 dark:text-gray-400">
        {{ priceLabel }}
      </div>
      <div class="mt-1 flex flex-wrap items-baseline gap-2">
        <span
          v-if="formattedOriginalPrice"
          class="text-sm font-semibold text-gray-500 line-through dark:text-gray-400"
        >
          {{ formattedOriginalPrice }}
        </span>
        <span class="text-2xl font-extrabold text-gray-900 dark:text-white">
          {{ formattedPrice }}
        </span>
      </div>

      <div :class="compactOnMobile ? 'mt-4' : 'mt-5'">
        <el-button
          class="flex w-full items-center justify-center rounded-xl py-2.5 text-sm font-semibold transition duration-150"
          :type="buttonType"
          :disabled="buttonDisabled"
          @click.stop="selectAddon"
        >
          {{ buttonText }}
        </el-button>
      </div>

      <div class="mt-4 flex flex-grow flex-col gap-2">
        <div class="flex items-center gap-2.5">
          <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white">
            <Check class="h-4 w-4" />
          </div>
          <span class="text-sm font-semibold leading-6 text-gray-700 dark:text-gray-200">
            {{ balanceText }}
          </span>
        </div>

        <template v-if="hasTrainingCapacity">
          <div class="flex items-center gap-2.5">
            <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white">
              <Check class="h-4 w-4" />
            </div>
            <span class="text-sm leading-6 text-gray-700 dark:text-gray-200">
              {{ upToLabel }}
              <strong class="text-base text-blue-600 dark:text-blue-400">
                {{ formattedInterviewCapacity }}
              </strong>
              {{ interviewTrainingsLabel }}
            </span>
          </div>

          <div class="flex items-center gap-2.5">
            <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-white">
              <Check class="h-4 w-4" />
            </div>
            <span class="text-sm leading-6 text-gray-700 dark:text-gray-200">
              {{ upToLabel }}
              <strong class="text-base text-blue-600 dark:text-blue-400">
                {{ formattedDialogCapacity }}
              </strong>
              {{ dialogTrainingsLabel }}
            </span>
          </div>
        </template>

        <div
          v-else
          class="text-xs text-gray-500 dark:text-gray-400"
        >
          {{ capacityUnavailableText }}
        </div>
      </div>
    </div>
  </div>
</template>
